import type { PortfolioAccount } from '@/modules/projects/components/portfolio/types'
import type { Transaction } from '@/modules/projects/stores/types'
import { isSecuritiesTrade } from '@/modules/projects/utils'
import { statementsSharingAMonth } from './accountTimelines'
import { monthLabel } from './months'
import { pairClosestUnlessTied } from './pairClosest'
import type {
  AccountStatement,
  AccountTimeline,
  ActivityEntry,
  ActivityKind,
  DateWindow,
  HiddenCopyReason,
  MoneyDirection,
  VerifyingStatement,
} from './types'

const SECURITIES_KINDS: Record<string, ActivityKind> = {
  security_purchase: 'buy',
  security_sale: 'sell',
  unsettled_trade: 'unsettled_trade',
  dividend: 'dividend',
  reinvestment: 'reinvestment',
  interest: 'interest',
  advisory_fee: 'fee',
  other_fee: 'fee',
  contribution: 'contribution',
  distribution: 'distribution',
  sweep: 'sweep',
  other: 'other',
}

export const securitiesKind = (activityType: string): ActivityKind =>
  SECURITIES_KINDS[activityType.trim().toLowerCase()] ?? 'other'

// lukita stores the enricher's category taxonomy humanized: INCOME_DIVIDENDS arrives as category
// "Income", detail "Dividends". Every key here is lowercased.
const INCOME_KINDS: Record<string, ActivityKind> = {
  dividends: 'dividend',
  'interest earned': 'interest',
}

const ACCOUNT_TRANSFER_DETAILS = new Set(['account transfer', 'savings'])

// A ledger row as lukita really sends it. Transaction types these as strings, but lukita leaves
// category, detail and date null on rows the enricher never labeled (brokerage 2: all 394 rows), and
// a paged row whose statement is not on its page has no file name or link.
export type LedgerRow = Pick<
  Transaction,
  | 'id'
  | 'accountType'
  | 'accountNumber'
  | 'amount'
  | 'kind'
  | 'description'
  | 'statementId'
  | 'sourcePage'
  | 'discardedAt'
  | 'internalTransfer'
> & {
  category: string | null
  categoryDetail: string | null
  date: string | null
  statementFileName?: string | null
  statementFileUrl?: string | null
}

type LedgerFields = Pick<
  Transaction,
  'accountType' | 'kind' | 'category' | 'categoryDetail' | 'internalTransfer'
>

// lukita's credit / debit column. lukita sets it from the amount's sign before each save, so it is
// the sign of the cash effect, nothing more.
const directionOf = (
  transaction: Pick<LedgerFields, 'kind'>,
): MoneyDirection | null => {
  const kind = (transaction.kind ?? '').toLowerCase()
  if (kind === 'credit') return 'in'
  if (kind === 'debit') return 'out'
  return null
}

const plainMovementKind = (
  accountType: LedgerFields['accountType'],
  direction: MoneyDirection,
): ActivityKind => {
  if (accountType === 'Retirement') {
    return direction === 'in' ? 'contribution' : 'distribution'
  }
  return direction === 'in' ? 'deposit' : 'withdrawal'
}

type CategoryRule = (
  detail: string,
  transaction: LedgerFields,
  direction: MoneyDirection,
) => ActivityKind

const transferKind: CategoryRule = (detail, transaction, direction) => {
  if (ACCOUNT_TRANSFER_DETAILS.has(detail)) {
    return direction === 'in' ? 'transfer_in' : 'transfer_out'
  }
  return plainMovementKind(transaction.accountType, direction)
}

// The categories this screen reads. Any other category is Other, never a deposit or a withdrawal.
const CATEGORY_RULES: Record<string, CategoryRule> = {
  income: (detail) => INCOME_KINDS[detail] ?? 'other',
  'bank fees': (detail) => (detail === 'interest charge' ? 'interest' : 'fee'),
  'government and non profit': (detail) =>
    detail === 'tax payment' ? 'fee' : 'other',
  'general services': (detail) =>
    detail === 'accounting and financial planning' ? 'fee' : 'other',
  'transfer in': transferKind,
  'transfer out': transferKind,
}

const labeledLedgerKind = (
  transaction: LedgerFields,
  direction: MoneyDirection,
): ActivityKind => {
  if (isSecuritiesTrade(transaction)) {
    return direction === 'out' ? 'buy' : 'sell'
  }
  const rule = CATEGORY_RULES[transaction.category.toLowerCase()]
  if (!rule) return 'other'
  return rule(transaction.categoryDetail.toLowerCase(), transaction, direction)
}

// lukita writes "Unknown" when the enricher gave no answer.
const hasCategory = (category: string | null): category is string =>
  !!category && category.toLowerCase() !== 'unknown'

// The Type of a cash ledger row, from the category the ledger sends. Never from the text. A row
// with no category is Other: its direction says only that money moved, not that it was a deposit.
export const ledgerKind = (
  transaction: Pick<LedgerRow, keyof LedgerFields>,
): ActivityKind => {
  if (transaction.internalTransfer) return 'sweep'
  const direction = directionOf(transaction)
  if (!direction || !hasCategory(transaction.category)) return 'other'
  return labeledLedgerKind(
    {
      ...transaction,
      category: transaction.category,
      categoryDetail: transaction.categoryDetail ?? '',
    },
    direction,
  )
}

interface StatementLabels {
  bySegment: Map<number, string>
  byStatement: Map<number, string>
}

// "Statement 1 of 2 for Dec 2022" for each statement that shares its month with another one, so the
// rows of both stay in the list and the reader can tell them apart.
const statementLabelsOf = (timeline: AccountTimeline): StatementLabels => {
  const bySegment = new Map<number, string>()
  const byStatement = new Map<number, string>()
  for (const group of statementsSharingAMonth(timeline)) {
    // Two cycles of one file: a ledger row names only the file, so it cannot be told apart.
    const canLabelLedger =
      new Set(group.map((statement) => statement.statementId)).size ===
      group.length
    group.forEach((statement, position) => {
      const label = `Statement ${position + 1} of ${group.length} for ${monthLabel(statement.month)}`
      bySegment.set(statement.segmentId, label)
      if (canLabelLedger) byStatement.set(statement.statementId, label)
    })
  }
  return { bySegment, byStatement }
}

type LabelsByAccount = Map<string, StatementLabels>

const statementWindow = (statement: {
  startDate: string | null
  endDate: string | null
}): DateWindow | null =>
  statement.endDate
    ? { from: statement.startDate ?? statement.endDate, to: statement.endDate }
    : null

const securitiesEntries = (
  account: PortfolioAccount,
  timeline: AccountTimeline,
  labels: StatementLabels | undefined,
): ActivityEntry[] =>
  account.periods.flatMap((period) =>
    period.activity.map((activity) => ({
      id: `securities:${activity.id}`,
      source: 'securities' as const,
      accountId: account.id,
      accountName: timeline.name,
      date: activity.date,
      settlementDate: activity.settlementDate,
      kind: securitiesKind(activity.type),
      description: activity.description,
      symbol: activity.symbol ?? '',
      quantity: activity.quantity ?? null,
      price: activity.price,
      amount: activity.amount,
      commission: activity.commissionFees,
      statementFileName: activity.statementFileName,
      statementFileUrl: activity.statementFileUrl,
      sourcePage: activity.sourcePage,
      pairing: null,
      statementLabel: labels?.bySegment.get(period.segmentId) ?? null,
      statementPeriod: statementWindow(period),
    })),
  )

const BROKERAGE_TYPES = new Set(['Investment', 'Retirement'])

// A row with no date cannot be told apart from the file's verifying cycle, so it waits with it.
const isWithinVerifyingStatement = (
  transaction: LedgerRow,
  verifying: VerifyingStatement[],
) =>
  verifying.some(
    (statement) =>
      statement.statementId === transaction.statementId &&
      (!transaction.date ||
        (transaction.date >= (statement.startDate ?? statement.endDate) &&
          transaction.date <= statement.endDate)),
  )

// A ledger row names only its file, so a file holding two cycles of the account gives no one period.
const ledgerStatementPeriod = (
  transaction: LedgerRow,
  timeline: AccountTimeline,
): DateWindow | null => {
  const statements = timeline.statements.filter(
    (statement) => statement.statementId === transaction.statementId,
  )
  return statements.length === 1 ? statementWindow(statements[0]) : null
}

// The money in or out of a row the enricher never sorted, so it is still counted on its own line.
const unsortedDirectionOf = (
  transaction: LedgerRow,
): { unsortedDirection?: MoneyDirection } => {
  const direction = directionOf(transaction)
  const isUnsorted =
    direction &&
    !transaction.internalTransfer &&
    !hasCategory(transaction.category)
  return isUnsorted ? { unsortedDirection: direction } : {}
}

const toLedgerEntry = (
  transaction: LedgerRow,
  timeline: AccountTimeline,
  labels: StatementLabels | undefined,
  statementPeriod: DateWindow | null,
): ActivityEntry => ({
  id: `ledger:${transaction.id}`,
  source: 'cash_ledger',
  accountId: timeline.id,
  accountName: timeline.name,
  date: transaction.date?.slice(0, 10) ?? '',
  settlementDate: '',
  kind: ledgerKind(transaction),
  description: transaction.description,
  symbol: '',
  quantity: null,
  price: null,
  amount: transaction.amount,
  commission: null,
  statementFileName: transaction.statementFileName ?? '',
  statementFileUrl: transaction.statementFileUrl ?? '',
  sourcePage: transaction.sourcePage ?? null,
  pairing: null,
  statementLabel: labels?.byStatement.get(transaction.statementId) ?? null,
  statementPeriod,
  ...unsortedDirectionOf(transaction),
})

const groupBy = <Item>(items: Item[], keyOf: (item: Item) => string) => {
  const groups = new Map<string, Item[]>()
  for (const item of items) {
    const key = keyOf(item)
    const group = groups.get(key)
    if (group) group.push(item)
    else groups.set(key, [item])
  }
  return groups
}

type StatementIdsByAccount = Map<string, Set<number>>

const statementIdsByAccount = (
  timelines: AccountTimeline[],
): StatementIdsByAccount =>
  new Map(
    timelines.map((timeline) => [
      timeline.id,
      new Set(timeline.statements.map((statement) => statement.statementId)),
    ]),
  )

// The portfolio account a ledger row belongs to: the one with its last 4 whose statement printed the
// row (the ledger can type a brokerage statement's cash rows as checking or savings), or the only one
// with its last 4 when the ledger typed the row as a brokerage account.
const ownerOf = (
  transaction: LedgerRow,
  candidates: AccountTimeline[],
  statementIds: StatementIdsByAccount,
): AccountTimeline | null => {
  const printedBy = candidates.filter((timeline) =>
    statementIds.get(timeline.id)?.has(transaction.statementId),
  )
  if (printedBy.length > 0) return printedBy.length === 1 ? printedBy[0] : null
  if (candidates.length !== 1) return null
  return BROKERAGE_TYPES.has(transaction.accountType) ? candidates[0] : null
}

// Ledger rows are told apart by statement record, securities rows by segment, both per account.
const ledgerStatementKey = (accountId: string, statementId: number) =>
  `${accountId}|ledger|${statementId}`

const securitiesStatementKey = (accountId: string, segmentId: number) =>
  `${accountId}|securities|${segmentId}`

type RowsByStatement = Map<string, ActivityEntry[]>

const addToStatement = (
  rowsByStatement: RowsByStatement,
  key: string,
  entry: ActivityEntry,
) => {
  const rows = rowsByStatement.get(key)
  if (rows) rows.push(entry)
  else rowsByStatement.set(key, [entry])
}

// The cash ledger rows of the portfolio's own accounts. A row from a statement that is still being
// verified is left out, like the rest of that statement. A row with no date is placed by its
// statement, so it is left out when that statement has no one period on the account.
const ledgerEntries = (
  transactions: LedgerRow[],
  timelines: AccountTimeline[],
  labelsByAccount: LabelsByAccount,
  rowsByStatement: RowsByStatement,
): ActivityEntry[] => {
  const timelinesByLast4 = groupBy(timelines, (timeline) => timeline.last4)
  const statementIds = statementIdsByAccount(timelines)
  const entries: ActivityEntry[] = []
  for (const transaction of transactions) {
    if (transaction.discardedAt || !transaction.accountNumber) continue
    const candidates = timelinesByLast4.get(transaction.accountNumber) ?? []
    const timeline = ownerOf(transaction, candidates, statementIds)
    if (!timeline) continue
    if (isWithinVerifyingStatement(transaction, timeline.verifying)) continue
    const statementPeriod = ledgerStatementPeriod(transaction, timeline)
    if (!transaction.date && !statementPeriod) continue
    const entry = toLedgerEntry(
      transaction,
      timeline,
      labelsByAccount.get(timeline.id),
      statementPeriod,
    )
    entries.push(entry)
    addToStatement(
      rowsByStatement,
      ledgerStatementKey(timeline.id, transaction.statementId),
      entry,
    )
  }
  return entries
}

const cents = (amount: number) => Math.round(amount * 100)

const rowKey = (entry: ActivityEntry) =>
  `${entry.source}|${cents(entry.amount ?? 0)}|${entry.date}`

// Row for row: each row of the first statement can be repeated by one row of the later one.
const repeatsOfFirst = (
  firstRows: ActivityEntry[],
  laterRows: ActivityEntry[],
): ActivityEntry[] => {
  const unmatched = new Map<string, number>()
  for (const entry of firstRows) {
    unmatched.set(rowKey(entry), (unmatched.get(rowKey(entry)) ?? 0) + 1)
  }
  return laterRows.filter((entry) => {
    const left = unmatched.get(rowKey(entry)) ?? 0
    if (entry.amount == null || left === 0) return false
    unmatched.set(rowKey(entry), left - 1)
    return true
  })
}

type HiddenRows = Map<string, HiddenCopyReason>

// Two statement records of one account for the same month (the same file uploaded twice). A row of
// a later one with the same amount and date as a row of the first is a copy, so its money counts
// once. The month stays Needs review for the two statements either way.
const secondStatementCopies = (
  timelines: AccountTimeline[],
  rowsByStatement: RowsByStatement,
): HiddenRows => {
  const hidden: HiddenRows = new Map()
  for (const timeline of timelines) {
    for (const [first, ...later] of statementsSharingAMonth(timeline)) {
      // Two cycles of one file share a statement record, so their ledger rows cannot be told apart.
      const canMatchLedger = later.every(
        (statement) => statement.statementId !== first.statementId,
      )
      const rowsOf = (statement: AccountStatement) => [
        ...(rowsByStatement.get(
          securitiesStatementKey(timeline.id, statement.segmentId),
        ) ?? []),
        ...((canMatchLedger &&
          rowsByStatement.get(
            ledgerStatementKey(timeline.id, statement.statementId),
          )) ||
          []),
      ]
      for (const statement of later) {
        for (const copy of repeatsOfFirst(rowsOf(first), rowsOf(statement))) {
          hidden.set(copy.id, {
            kind: 'second_statement',
            month: statement.month,
          })
        }
      }
    }
  }
  return hidden
}

// Trades settle a few days after the trade date, around weekends and holidays.
const CASH_LEG_MAX_DAYS_APART = 7

const cashLegKey = (entry: ActivityEntry) =>
  `${entry.accountId}|${cents(entry.amount ?? 0)}`

const isCashLegCandidate = (entry: ActivityEntry) =>
  entry.amount != null && entry.amount !== 0 && entry.date !== ''

// On a brokerage account the securities rows are the record and the cash ledger repeats their cash.
// A ledger row of the same account and signed cents, a week apart at most, is the cash leg of that
// securities row whatever the enricher labeled it (it calls a Microsoft buy "Electronics"), so the
// ledger row is hidden. When kroos typed the securities row Other, that row is hidden instead, so the
// ledger row's type stands. Closest first; a tie hides nothing. Sweeps on the ledger always stay.
const cashLegs = (
  securities: ActivityEntry[],
  ledger: ActivityEntry[],
): HiddenRows => {
  const ledgerGroups = groupBy(
    ledger.filter(
      (entry) => isCashLegCandidate(entry) && entry.kind !== 'sweep',
    ),
    cashLegKey,
  )
  const securitiesGroups = groupBy(
    securities.filter(isCashLegCandidate),
    cashLegKey,
  )
  const hidden: HiddenRows = new Map()
  for (const [key, ledgerGroup] of ledgerGroups) {
    const securitiesGroup = securitiesGroups.get(key)
    if (!securitiesGroup) continue
    const pairs = pairClosestUnlessTied(ledgerGroup, securitiesGroup, {
      maxDaysApart: CASH_LEG_MAX_DAYS_APART,
      canPair: () => true,
      datesOf: (entry) =>
        entry.settlementDate
          ? [entry.date, entry.settlementDate]
          : [entry.date],
    })
    for (const [ledgerEntry, securitiesEntry] of pairs) {
      if (securitiesEntry.kind === 'other') {
        hidden.set(securitiesEntry.id, { kind: 'securities_copy' })
      } else {
        hidden.set(ledgerEntry.id, { kind: 'cash_leg' })
      }
    }
  }
  return hidden
}

const securitiesByStatement = (
  accounts: PortfolioAccount[],
  rowsByStatement: RowsByStatement,
  entryById: Map<string, ActivityEntry>,
) => {
  for (const account of accounts) {
    for (const period of account.periods) {
      for (const activity of period.activity) {
        const entry = entryById.get(`securities:${activity.id}`)
        if (!entry) continue
        addToStatement(
          rowsByStatement,
          securitiesStatementKey(account.id, period.segmentId),
          entry,
        )
      }
    }
  }
}

const splitHidden = (all: ActivityEntry[], hidden: HiddenRows) => ({
  entries: all.filter((entry) => !hidden.has(entry.id)),
  hiddenCopies: all.flatMap((entry) => {
    const hiddenAs = hidden.get(entry.id)
    return hiddenAs ? [{ ...entry, hiddenAs }] : []
  }),
})

// One list per project: every account's securities rows and cash ledger rows, each event once.
// Copies from a second statement go first, so the cash legs pair inside one statement.
export const mergeActivity = (
  accounts: PortfolioAccount[],
  timelines: AccountTimeline[],
  transactions: LedgerRow[],
): { entries: ActivityEntry[]; hiddenCopies: ActivityEntry[] } => {
  const timelineById = new Map(
    timelines.map((timeline) => [timeline.id, timeline]),
  )
  const labelsByAccount: LabelsByAccount = new Map(
    timelines.map((timeline) => [timeline.id, statementLabelsOf(timeline)]),
  )
  const securities = accounts.flatMap((account) => {
    const timeline = timelineById.get(account.id)
    return timeline
      ? securitiesEntries(account, timeline, labelsByAccount.get(account.id))
      : []
  })
  const rowsByStatement: RowsByStatement = new Map()
  securitiesByStatement(
    accounts,
    rowsByStatement,
    new Map(securities.map((entry) => [entry.id, entry])),
  )
  const ledger = ledgerEntries(
    transactions,
    timelines,
    labelsByAccount,
    rowsByStatement,
  )
  const hidden = secondStatementCopies(timelines, rowsByStatement)
  const isShown = (entry: ActivityEntry) => !hidden.has(entry.id)
  for (const [id, reason] of cashLegs(
    securities.filter(isShown),
    ledger.filter(isShown),
  )) {
    hidden.set(id, reason)
  }
  return splitHidden([...securities, ...ledger], hidden)
}
